import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Shield, ArrowLeft } from "lucide-react";

function Navigation() {
  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link href="/" data-testid="link-home">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground">
            Features
          </Link>
          <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground">
            Pricing
          </Link>
          <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground">
            Docs
          </Link>
          <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground">
            Blog
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link href="/login">
            <Button variant="ghost" size="sm">
              Login
            </Button>
          </Link>
          <Link href="/signup">
            <Button size="sm">
              Sign Up Free
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
          <nav className="flex flex-wrap items-center justify-center gap-6">
            <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground">
              Features
            </Link>
            <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground">
              Pricing
            </Link>
            <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground">
              Docs
            </Link>
            <Link href="/contact" className="text-sm text-muted-foreground hover:text-foreground">
              Contact
            </Link>
            <Link href="/privacy" className="text-sm text-foreground font-medium">
              Privacy
            </Link>
            <Link href="/terms" className="text-sm text-muted-foreground hover:text-foreground">
              Terms
            </Link>
          </nav>
          <div className="text-sm text-muted-foreground">
            {new Date().getFullYear()} ReviewKit. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <Link href="/">
            <Button variant="ghost" size="sm" className="gap-2 mb-4">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-md bg-primary/10 flex items-center justify-center">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Privacy Policy</h1>
              <p className="text-muted-foreground mt-1">Last Updated: December 23, 2025</p>
            </div>
          </div>
        </div>

        <article className="prose prose-neutral dark:prose-invert max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
            <p className="text-muted-foreground">
              This Privacy Policy explains how <strong>Plumbumpy Properties LLC</strong>, a Colorado limited liability company doing business as <strong>ReviewKit</strong> ("Company," "we," "us," or "our"), collects, uses, discloses, and protects your information when you use our service at reviewkit.io (the "Service").
            </p>
            <p className="text-muted-foreground mt-4">
              By using ReviewKit, you agree to the collection and use of information in accordance with this Privacy Policy.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Data Controller</h2>
            <p className="text-muted-foreground">
              For purposes of applicable data protection laws, the data controller is:
            </p>
            <p className="text-muted-foreground mt-4">
              <strong>Plumbumpy Properties LLC dba ReviewKit</strong><br />
              Boulder, CO, United States<br />
              Email: <a href="mailto:privacy@reviewkit.io" className="text-primary hover:underline">privacy@reviewkit.io</a>
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Information We Collect</h2>
            
            <h3 className="text-lg font-medium mt-6 mb-3">1.1 Information You Provide</h3>
            <p className="text-muted-foreground mb-3"><strong>Account Information:</strong></p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>Name</li>
              <li>Email address</li>
              <li>Password (encrypted)</li>
              <li>Business name</li>
              <li>Business address (optional)</li>
            </ul>

            <p className="text-muted-foreground mb-3"><strong>Business Data:</strong></p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>Business logo</li>
              <li>Brand colors</li>
              <li>Google Place ID</li>
              <li>Staff member names</li>
            </ul>

            <p className="text-muted-foreground mb-3"><strong>Customer Data You Enter:</strong></p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>Customer names</li>
              <li>Customer email addresses</li>
              <li>Job descriptions</li>
              <li>Job photos (optional)</li>
            </ul>

            <p className="text-muted-foreground mb-3"><strong>Payment Information:</strong></p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Processed securely by Stripe</li>
              <li>We do not store credit card numbers</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">1.2 Information Collected Automatically</h3>
            <p className="text-muted-foreground mb-3"><strong>Usage Data:</strong></p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>Pages visited</li>
              <li>Features used</li>
              <li>Time and date of access</li>
              <li>Referring website</li>
              <li>Browser type and version</li>
              <li>Device type</li>
            </ul>

            <p className="text-muted-foreground mb-3"><strong>Email Analytics:</strong></p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>Email open rates</li>
              <li>Click-through rates</li>
              <li>Delivery status</li>
            </ul>

            <p className="text-muted-foreground mb-3"><strong>Review Flow Data:</strong></p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Star ratings selected by your customers</li>
              <li>Private feedback submitted</li>
              <li>Google review clicks</li>
              <li>QR code scans</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">1.3 Information from Third Parties</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Google Places API:</strong> Business information when you search for and connect your business</li>
              <li><strong>Stripe:</strong> Payment confirmation and subscription status</li>
            </ul>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. How We Use Your Information</h2>
            <p className="text-muted-foreground mb-4">We use the information we collect to:</p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-border mb-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border px-4 py-2 text-left">Purpose</th>
                    <th className="border border-border px-4 py-2 text-left">Legal Basis</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr>
                    <td className="border border-border px-4 py-2">Provide and maintain the Service</td>
                    <td className="border border-border px-4 py-2">Contract performance</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Process your payments</td>
                    <td className="border border-border px-4 py-2">Contract performance</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Send review request emails on your behalf</td>
                    <td className="border border-border px-4 py-2">Contract performance</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Analyze and improve the Service</td>
                    <td className="border border-border px-4 py-2">Legitimate interest</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Send you service-related communications</td>
                    <td className="border border-border px-4 py-2">Contract performance</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Respond to your inquiries and support requests</td>
                    <td className="border border-border px-4 py-2">Contract performance</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Detect and prevent fraud or abuse</td>
                    <td className="border border-border px-4 py-2">Legitimate interest</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Comply with legal obligations</td>
                    <td className="border border-border px-4 py-2">Legal obligation</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. How We Share Your Information</h2>
            <p className="text-muted-foreground mb-4">
              <strong>We do not sell your personal information.</strong> We may share your information with:
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">3.1 Service Providers</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-border mb-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border px-4 py-2 text-left">Provider</th>
                    <th className="border border-border px-4 py-2 text-left">Purpose</th>
                    <th className="border border-border px-4 py-2 text-left">Data Shared</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr>
                    <td className="border border-border px-4 py-2"><strong>Supabase</strong></td>
                    <td className="border border-border px-4 py-2">Database hosting and authentication</td>
                    <td className="border border-border px-4 py-2">Account data, business data, customer data</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2"><strong>Resend</strong></td>
                    <td className="border border-border px-4 py-2">Email delivery</td>
                    <td className="border border-border px-4 py-2">Customer emails, email content</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2"><strong>Stripe</strong></td>
                    <td className="border border-border px-4 py-2">Payment processing</td>
                    <td className="border border-border px-4 py-2">Payment details, subscription info</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2"><strong>Google</strong></td>
                    <td className="border border-border px-4 py-2">Places API for business search</td>
                    <td className="border border-border px-4 py-2">Business search queries</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2"><strong>Vercel</strong></td>
                    <td className="border border-border px-4 py-2">Website hosting</td>
                    <td className="border border-border px-4 py-2">Usage logs, IP addresses</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-muted-foreground">
              All service providers are contractually obligated to protect your data and use it only for the purposes we specify.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">3.2 Legal Requirements</h3>
            <p className="text-muted-foreground mb-2">We may disclose your information if required to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Comply with a legal obligation, subpoena, or court order</li>
              <li>Protect and defend our rights or property</li>
              <li>Prevent or investigate possible wrongdoing</li>
              <li>Protect the personal safety of users or the public</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">3.3 Business Transfers</h3>
            <p className="text-muted-foreground">
              If we are involved in a merger, acquisition, or sale of assets, your information may be transferred. We will provide notice before your information becomes subject to a different privacy policy.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. Data Storage and Security</h2>

            <h3 className="text-lg font-medium mt-6 mb-3">4.1 Where We Store Data</h3>
            <p className="text-muted-foreground mb-4">
              Your data is stored on servers located in the <strong>United States</strong> through our service provider Supabase. If you are located outside the United States, your information will be transferred to and processed in the United States.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">4.2 Security Measures</h3>
            <p className="text-muted-foreground mb-2">We implement appropriate technical and organizational measures to protect your data, including:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Encryption of data in transit (TLS/SSL)</li>
              <li>Encryption of data at rest</li>
              <li>Secure password hashing</li>
              <li>Access controls and authentication</li>
              <li>Regular security assessments</li>
              <li>Row-level security on database tables</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">4.3 Security Limitations</h3>
            <p className="text-muted-foreground">
              While we strive to protect your information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. Data Retention</h2>
            <p className="text-muted-foreground mb-4">We retain your information for as long as necessary to:</p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-border mb-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border px-4 py-2 text-left">Data Type</th>
                    <th className="border border-border px-4 py-2 text-left">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr>
                    <td className="border border-border px-4 py-2">Account data</td>
                    <td className="border border-border px-4 py-2">Until you delete your account + 30 days</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Business data</td>
                    <td className="border border-border px-4 py-2">Until you delete your account + 30 days</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Customer data</td>
                    <td className="border border-border px-4 py-2">Until you delete it or your account + 30 days</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Review interactions</td>
                    <td className="border border-border px-4 py-2">3 years</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Email logs</td>
                    <td className="border border-border px-4 py-2">1 year</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Payment records</td>
                    <td className="border border-border px-4 py-2">7 years (legal requirement)</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Usage analytics</td>
                    <td className="border border-border px-4 py-2">2 years</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-muted-foreground">
              After account deletion, we may retain anonymized, aggregated data for analytics purposes.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Your Rights</h2>
            <p className="text-muted-foreground mb-4">Depending on your location, you may have the following rights:</p>

            <h3 className="text-lg font-medium mt-6 mb-3">6.1 All Users</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your data</li>
              <li><strong>Export:</strong> Request your data in a portable format</li>
              <li><strong>Objection:</strong> Object to certain processing of your data</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">6.2 California Residents (CCPA)</h3>
            <p className="text-muted-foreground mb-2">If you are a California resident, you have the right to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Know what personal information we collect about you</li>
              <li>Know whether your personal information is sold or disclosed</li>
              <li>Say no to the sale of personal information (we do not sell your data)</li>
              <li>Access your personal information</li>
              <li>Request deletion of your personal information</li>
              <li>Not be discriminated against for exercising your privacy rights</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              To exercise these rights, contact us at <a href="mailto:privacy@reviewkit.io" className="text-primary hover:underline">privacy@reviewkit.io</a>.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">6.3 European Users (GDPR)</h3>
            <p className="text-muted-foreground mb-2">If you are in the European Economic Area, you have additional rights:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Right to be forgotten:</strong> Request erasure of your data</li>
              <li><strong>Right to restrict processing:</strong> Limit how we use your data</li>
              <li><strong>Right to data portability:</strong> Receive your data in a structured format</li>
              <li><strong>Right to withdraw consent:</strong> Where processing is based on consent</li>
              <li><strong>Right to lodge a complaint:</strong> With your local data protection authority</li>
            </ul>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Customer Data (Data You Collect)</h2>

            <h3 className="text-lg font-medium mt-6 mb-3">7.1 Your Responsibilities</h3>
            <p className="text-muted-foreground mb-2">
              When you use ReviewKit to collect customer information, <strong>you are the data controller</strong> for that customer data. You are responsible for:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Obtaining proper consent from your customers</li>
              <li>Providing your customers with privacy notices</li>
              <li>Responding to data subject requests from your customers</li>
              <li>Complying with applicable privacy laws</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">7.2 Our Role</h3>
            <p className="text-muted-foreground">
              We act as a <strong>data processor</strong> for customer data you enter into ReviewKit. We process this data only according to your instructions and these Terms.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">7.3 Customer Inquiries</h3>
            <p className="text-muted-foreground">
              If one of your customers contacts us about their data, we will direct them to you unless we are legally required to respond directly.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. Cookies and Tracking</h2>

            <h3 className="text-lg font-medium mt-6 mb-3">8.1 What We Use</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-border mb-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border px-4 py-2 text-left">Cookie/Technology</th>
                    <th className="border border-border px-4 py-2 text-left">Purpose</th>
                    <th className="border border-border px-4 py-2 text-left">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr>
                    <td className="border border-border px-4 py-2">Session cookies</td>
                    <td className="border border-border px-4 py-2">Authentication</td>
                    <td className="border border-border px-4 py-2">Session</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Preference cookies</td>
                    <td className="border border-border px-4 py-2">Remember your settings</td>
                    <td className="border border-border px-4 py-2">1 year</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Analytics</td>
                    <td className="border border-border px-4 py-2">Understand usage patterns</td>
                    <td className="border border-border px-4 py-2">2 years</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-medium mt-6 mb-3">8.2 Your Choices</h3>
            <p className="text-muted-foreground">
              Most browsers allow you to control cookies through settings. Note that disabling cookies may affect Service functionality.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">8.3 Do Not Track</h3>
            <p className="text-muted-foreground">
              We do not currently respond to "Do Not Track" browser signals.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. Third-Party Links</h2>
            <p className="text-muted-foreground">
              The Service may contain links to third-party websites (such as Google Reviews). We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Children's Privacy</h2>
            <p className="text-muted-foreground">
              ReviewKit is not intended for individuals under 16 years of age. We do not knowingly collect personal information from children under 16. If you believe we have collected information from a child under 16, please contact us immediately at <a href="mailto:privacy@reviewkit.io" className="text-primary hover:underline">privacy@reviewkit.io</a>.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. International Data Transfers</h2>
            <p className="text-muted-foreground mb-4">
              If you are located outside the United States, your information will be transferred to and processed in the United States. By using the Service, you consent to this transfer.
            </p>
            <p className="text-muted-foreground">For transfers from the EEA, we rely on:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Standard Contractual Clauses approved by the European Commission</li>
              <li>The necessity of the transfer for contract performance</li>
            </ul>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-muted-foreground mb-4">We may update this Privacy Policy from time to time. We will notify you of material changes by:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Posting the new Privacy Policy on this page</li>
              <li>Updating the "Last Updated" date</li>
              <li>Sending an email notification for significant changes</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">13. Contact Us</h2>
            <p className="text-muted-foreground mb-4">
              If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <p className="text-muted-foreground">
              <strong>Plumbumpy Properties LLC dba ReviewKit</strong><br />
              Privacy Inquiries: <a href="mailto:privacy@reviewkit.io" className="text-primary hover:underline">privacy@reviewkit.io</a><br />
              General Support: <a href="mailto:support@reviewkit.io" className="text-primary hover:underline">support@reviewkit.io</a><br />
              Legal Matters: <a href="mailto:legal@reviewkit.io" className="text-primary hover:underline">legal@reviewkit.io</a><br />
              Website: <a href="https://reviewkit.io" className="text-primary hover:underline">https://reviewkit.io</a>
            </p>
            <p className="text-muted-foreground mt-4">
              <strong>Response Time:</strong> We aim to respond to all privacy-related inquiries within 30 days.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">14. Supplemental Notices</h2>

            <h3 className="text-lg font-medium mt-6 mb-3">14.1 Notice to California Residents</h3>
            <p className="text-muted-foreground mb-3"><strong>Categories of Personal Information Collected:</strong></p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>Identifiers (name, email, IP address)</li>
              <li>Commercial information (purchase history)</li>
              <li>Internet activity (usage data)</li>
            </ul>

            <p className="text-muted-foreground mb-3"><strong>Categories of Sources:</strong></p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>Directly from you</li>
              <li>Automatically through the Service</li>
              <li>Third-party service providers</li>
            </ul>

            <p className="text-muted-foreground mb-3"><strong>Business Purposes for Collection:</strong></p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-4">
              <li>Providing the Service</li>
              <li>Processing payments</li>
              <li>Analytics and improvement</li>
              <li>Security and fraud prevention</li>
            </ul>

            <p className="text-muted-foreground font-medium">We do not sell personal information.</p>

            <h3 className="text-lg font-medium mt-6 mb-3">14.2 Notice to Nevada Residents</h3>
            <p className="text-muted-foreground">
              We do not sell your personal information as defined under Nevada law. If you have questions, contact <a href="mailto:privacy@reviewkit.io" className="text-primary hover:underline">privacy@reviewkit.io</a>.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <p className="text-muted-foreground italic text-center">
            By using ReviewKit, you acknowledge that you have read and understood this Privacy Policy.
          </p>
        </article>
      </main>
      <Footer />
    </div>
  );
}


